import { throttle } from 'lodash';
import { useEffect, useRef, useState, useCallback } from 'react';

interface ScrollState {
  isScrollingUp: boolean;
  isScrollingDown: boolean;
  currentScrollY: number;
}

interface UseVerticalScrollOptions {
  passive?: boolean;
  throttleMs?: number;
}

export const useVerticalScroll = (
  options: UseVerticalScrollOptions = {},
): ScrollState => {
  const { passive = true, throttleMs = 150 } = options;
  const lastScrollY = useRef(0);

  const [currentScrollY, setCurrentScrollY] = useState(0);
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isScrollingUp, setIsScrollingUp] = useState(false);

  const handleScroll = useCallback(() => {
    const currentY = window.scrollY;
    const isUp = currentY < lastScrollY.current;

    setCurrentScrollY(currentY);
    setIsScrollingDown(!isUp);
    setIsScrollingUp(isUp);

    lastScrollY.current = currentY;
  }, []);

  const throttledScroll = throttle(handleScroll, throttleMs);

  useEffect(() => {
    window.addEventListener('scroll', throttledScroll, {
      passive,
    });

    return () => {
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [handleScroll, passive, throttleMs]);

  return {
    isScrollingUp,
    isScrollingDown,
    currentScrollY,
  };
};
